import { useCallback, ReactNode } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { t } from '../../../../i18n';
import { TmsTaskStatusEnum, TmsTaskWaypointStatusEnum } from '../../../../enums/TmsEnum';
import { useBolGenerationV3 } from './useBolGenerationV3';

/**
 * 任务页面共用逻辑 Hook
 * 提供任务页面通用的时间处理、时间比较、BOL 打印等功能
 */
export const useTaskCommon = () => {
    // 获取 BOL 生成 hook
    const { handleBolPrint } = useBolGenerationV3();
    /**
     * 比较实际时间和计划时间，返回颜色
     * planAt 和 actualAt 是 UTC-0 的时间戳字符串（ISO 8601 格式）
     */
    const compareTimeColor = useCallback(
        (planAt: string | null | undefined, actualAt: string | null | undefined): 'green' | 'red' | null => {
            // 如果任一值为空，不做判断
            if (!planAt || !actualAt) {
                return null;
            }

            // 解析 UTC-0 时间戳（ISO 8601 格式，如 "2025-11-14T11:08:59Z"）
            const planTime = dayjs(planAt);
            const actualTime = dayjs(actualAt);

            // 如果时间无效，不做判断
            if (!planTime.isValid() || !actualTime.isValid()) {
                return null;
            }

            // 只比较到分钟级别，忽略秒
            const planTimeMinute = planTime.startOf('minute');
            const actualTimeMinute = actualTime.startOf('minute');

            // 如果实际时间早于计划时间，显示绿色
            if (actualTimeMinute.isBefore(planTimeMinute)) {
                return 'green';
            }
            // 如果实际时间晚于计划时间，显示红色
            if (actualTimeMinute.isAfter(planTimeMinute)) {
                return 'red';
            }

            // 相等时不做颜色标记
            return null;
        },
        []
    );

    /**
     * 从本地时间字符串和时区缩写渲染时间，显示格式：日期+时间+timezone
     */
    const renderTimeFromUtcTimestamp = useCallback(
        (utcTimestamp: string | null | undefined, timezone: string | null | undefined, color?: 'green' | 'red' | null): ReactNode => {
            if (!utcTimestamp) return '-';

            try {
                // 解析并格式化时间戳为：YYYY/MM/DD HH:mm
                const formattedTime = dayjs(utcTimestamp).format('YYYY/MM/DD HH:mm');

                // 根据颜色设置样式
                const colorStyle =
                    color === 'green'
                        ? { color: '#52c41a' }
                        : color === 'red'
                            ? { color: '#ff4d4f' }
                            : {};

                // 显示格式：日期+时间+timezone（例如：11/14 08:00 (ET)）
                return (
                    <div style={colorStyle}>
                        <div>{formattedTime}</div>
                        {timezone && <div>({timezone.toUpperCase()})</div>}
                    </div>
                );
            } catch (e) {
                console.error('Error formatting time:', e);
                return '-';
            }
        },
        []
    );

    /**
     * 处理 BOL 打印并打开 modal
     */
    const handleBOLPrintWithModal = useCallback(
        async (
            record: any,
            handleBolPrint: (record: any) => Promise<{ success: boolean; error?: string; bolData?: any; task?: any }>,
            setBolData: (data: any) => void,
            setSelectedTaskForBOL: (task: any) => void,
            setBolModalOpen: (open: boolean) => void
        ) => {
            try {
                const result = await handleBolPrint(record);

                if (!result.success) {
                    message.error(result.error);
                    return;
                }

                setBolData(result.bolData || null);
                setSelectedTaskForBOL(result.task);
                setBolModalOpen(true);
            } catch (error) {
                console.error('Failed to get BOL infos:', error);
                message.error(t('tms.task.messages.failedToGetBolNumbers'));
            }
        },
        []
    );

    /**
     * 获取任务状态的样式类名（深色版本）- 用于 ShipFromHereTask
     */
    const getTaskStatusClassName = useCallback((taskStatus: number): string => {
        let className = '';
        switch (taskStatus) {
            case TmsTaskStatusEnum.Draft:
                className = 'bg-gray-100 text-gray-500 border-gray-300';
                break;
            case TmsTaskStatusEnum.Published:
                className = 'bg-blue-100 text-blue-700 border-blue-300';
                break;
            case TmsTaskStatusEnum.InProgress:
                className = 'bg-yellow-100 text-yellow-700 border-yellow-300';
                break;
            case TmsTaskStatusEnum.Completed:
                className = 'bg-lime-100 text-lime-700 border-lime-300';
                break;
            case TmsTaskStatusEnum.Abnormal:
                className = 'bg-rose-200 text-rose-700 border-rose-400';
                break;
            case TmsTaskStatusEnum.Cancelled:
                className = 'bg-gray-200 text-gray-600 border-gray-300';
                break;
            default:
                className = 'bg-gray-100 text-gray-600 border-gray-300';
        }
        return className;
    }, []);


    /**
     * 获取途经点状态的样式类名（与PDA模块保持一致）
     */
    const getWaypointStatusClassName = useCallback((waypointStatus: number): string => {
        switch (waypointStatus) {
            case TmsTaskWaypointStatusEnum.Planned:
                return 'bg-yellow-100 text-yellow-700 border-yellow-300';
            case TmsTaskWaypointStatusEnum.Arrived:
                return 'bg-green-100 text-green-700 border-green-300';
            case TmsTaskWaypointStatusEnum.Done:
                return 'bg-blue-100 text-blue-700 border-blue-300';
            case TmsTaskWaypointStatusEnum.PENDING_DEPARTURE:
                return 'bg-red-100 text-red-600 border-red-300'; // 此处是text-red-600，不是text-red-700，700有点太亮
            default:
                return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    }, []);

    /**
     * 获取途经点状态的文本
     */
    const getWaypointStatusText = useCallback((waypointStatus: number): string => {
        switch (waypointStatus) {
            case TmsTaskWaypointStatusEnum.Planned:
                return t('tms.task.waypointStatus.planned');
            case TmsTaskWaypointStatusEnum.Arrived:
                return t('tms.task.waypointStatus.arrived');
            case TmsTaskWaypointStatusEnum.Done:
                return t('tms.task.waypointStatus.done');
            case TmsTaskWaypointStatusEnum.PENDING_DEPARTURE:
                return t('tms.task.waypointStatus.pendingDeparture');
            default:
                return '-';
        }
    }, [t]);

    return {
        compareTimeColor,
        renderTimeFromUtcTimestamp,
        generateBolData: handleBOLPrintWithModal,
        getBolInfos: handleBolPrint,
        getTaskStatusClassName,
        getWaypointStatusClassName,
        getWaypointStatusText
    };
};

